import { Button, Dialog, Typography } from "@hellocarbo/lasagne";
import { ascendingSort } from "@hellocarbo/lasagne/utils";
import { Plus } from "lucide-react";
import { use } from "react";
import { useTranslation } from "react-i18next";

import { PortalContainerContext } from "@/contexts";
import { AllActionsBySteps } from "@/types";
import { getActionData } from "@/utils/onboardingActions";

import Action from "./Action";

interface ViewAllActionsModalProps {
  allActionsBySteps?: AllActionsBySteps;
  trackingCampaignId: number | null;
  scoreId: number | null;
  reviewId: number | null;
}

const ViewAllActionsModal = ({
  allActionsBySteps,
  trackingCampaignId,
  scoreId,
  reviewId,
}: ViewAllActionsModalProps) => {
  const { container } = use(PortalContainerContext);
  const { t } = useTranslation("onboarding");

  return (
    <Dialog
      trigger={
        <Button shape="link" intent="secondary" SuffixIcon={Plus}>
          {t("actions.view_all")}
        </Button>
      }
      title={t("actions.modal.title")}
      description={t("actions.modal.description")}
      portalContainer={container as Element}
      size="large"
    >
      <ul>
        {allActionsBySteps?.map((step, index) => {
          const sortedActions = step.actions.sort((a, b) =>
            ascendingSort(a.rank, b.rank)
          );

          return (
            <li className="mt-md" key={index}>
              <Typography step="1" as="p" weight="semibold" className="mb-xs">
                {t(`steps_list.${step.key}`)}
              </Typography>
              <ul className="space-y-sm">
                {sortedActions.map((action) => {
                  const { icon, url, isExternal } = getActionData({
                    actionId: action.key,
                    trackingCampaignId,
                    scoreId,
                    reviewId,
                  });

                  return (
                    <Action
                      key={action.key}
                      Icon={icon}
                      title={t(`actions.actions_list.${action.key}.title`)}
                      unavailableReason={t(
                        `actions.actions_list.${action.key}.unavailable_reason`
                      )}
                      actionKey={action.key}
                      isSkippable={action.is_skippable}
                      status={action.status}
                      href={url}
                      isExternal={isExternal}
                    />
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </Dialog>
  );
};

export default ViewAllActionsModal;
